import { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import "./App.scss";
import Main from "./pages/Main/Main";
import Home from "./pages/Home/Home";
import Admins from "./pages/Admins/Admins";
import Users from "./pages/Users/Users";
import ProductOrders from "./pages/ProductOrders/ProductOrders";
import Departments from "./pages/Departments/Departments";
import Services from "./pages/Services/Services";
import Settings from "./pages/Settings/Settings";
import Login from "./pages/Login/Login";
import Permissions from "./pages/Permissions/Permissions";
import User from "./pages/User/User";
import Workers from "./pages/Workers/Workers";
import DiscountCodes from "./pages/DiscountCodes/DiscountCodes";
import Notifications from "./pages/Notifications/Notifications";
import CreateNotification from "./pages/Notifications/CreateNotification/CreateNotification";
import ViewProductOrder from "./pages/ProductOrders/ViewProductOrder/ViewProductOrder";
import Support from "./pages/Support/Support";
import Packages from "./pages/Packages/Packages";
import CreatePackage from "./pages/Packages/CreatePackage/CreatePackage";
import Profile from "./pages/Profile/Profile";
import Messages from "./pages/Messages/Messages";
import Worker from "./pages/Worker/Worker";
import Subscriptions from "./pages/Subscriptions/Subscriptions";
import EditAddressRequest from "./pages/EditAddressRequest/EditAddressRequest";
import CreateMessage from "./pages/Messages/CreateMessage/CreateMessage";
import CancellationRequests from "./pages/CancellationRequests/CancellationRequests";
import InspectionRequests from "./pages/InspectionRequests/InspectionRequests";
import Ratings from "./pages/Ratings/Ratings";
import {
  connectSocket,
  disconnectSocket,
  selectIsConnected,
  selectIsConnecting,
} from "./redux/websocketSlice";
import { PermissionGuard } from "./hooks/usePermissions"; // Import the permission guard
import Parts from "./pages/Parts/Parts";

// Component for access denied
const AccessDenied = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "60vh",
      textAlign: "center",
    }}
  >
    <h2 style={{ color: "#d32f2f", marginBottom: "16px" }}>
      غير مسموح بالدخول
    </h2>
    <p style={{ color: "#666", fontSize: "16px" }}>
      ليس لديك صلاحية للوصول إلى هذه الصفحة
    </p>
  </div>
);

// Route protection wrapper component
const ProtectedRoute = ({
  children,
  permissionId,
  fallback = <AccessDenied />,
}) => {
  return (
    <PermissionGuard permission={permissionId} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

function App() {
  // Get authentication state from Redux
  const { isAuthenticated } = useSelector((state) => state.auth);
  const isConnected = useSelector(selectIsConnected);
  const isConnecting = useSelector(selectIsConnecting);
  const dispatch = useDispatch();

  // Setup socket connection when authenticated
  useEffect(() => {
    // Check if token exists in localStorage
    const token = localStorage.getItem("token");

    // Only connect if token exists, user is authenticated, and not already connected/connecting
    if (isAuthenticated && token && !isConnected && !isConnecting) {
      dispatch(connectSocket({ language: "en" })); // You can get language from context/state if needed
    }

    // Disconnect when user logs out or token is removed
    if ((!isAuthenticated || !token) && isConnected) {
      dispatch(disconnectSocket());
    }
  }, [isAuthenticated, isConnected, isConnecting, dispatch]);

  // Listen for localStorage changes (token changes)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token") {
        const token = e.newValue;

        if (token && isAuthenticated && !isConnected && !isConnecting) {
          dispatch(connectSocket({ language: "en" }));
        } else if (!token && isConnected) {
          dispatch(disconnectSocket());
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAuthenticated, isConnected, isConnecting, dispatch]);

  // Cleanup socket connection on unmount
  useEffect(() => {
    return () => {
      if (isConnected) {
        dispatch(disconnectSocket());
      }
    };
  }, [isConnected, dispatch]);

  const router = createBrowserRouter([
    {
      path: "/login",
      element: !isAuthenticated ? <Login /> : <Navigate to="/" />,
    },
    {
      path: "/",
      element: isAuthenticated ? <Main /> : <Navigate to="/login" />,
      children: [
        {
          path: "/",
          element: <Home />, // Dashboard is always accessible for authenticated users
        },
        {
          path: "/admins",
          element: (
            <ProtectedRoute permissionId={11}>
              <Admins />
            </ProtectedRoute>
          ),
        },
        {
          path: "/admins/permissions",
          element: (
            <ProtectedRoute permissionId={20}>
              <Permissions />
            </ProtectedRoute>
          ),
        },
        {
          path: "/admins/permissions/:id",
          element: (
            <ProtectedRoute permissionId={19}>
              <Permissions />
            </ProtectedRoute>
          ),
        },
        {
          path: "/users",
          element: (
            <ProtectedRoute permissionId={23}>
              <Users />
            </ProtectedRoute>
          ),
        },
        {
          path: "/users/:id",
          element: (
            <ProtectedRoute permissionId={28}>
              <User />
            </ProtectedRoute>
          ),
        },
        {
          path: "/workers",
          element: (
            <ProtectedRoute permissionId={29}>
              <Workers />
            </ProtectedRoute>
          ),
        },
        {
          path: "/workers/:id",
          element: (
            <ProtectedRoute permissionId={35}>
              <Worker />
            </ProtectedRoute>
          ),
        },
        {
          path: "/departments",
          element: (
            <ProtectedRoute permissionId={38}>
              <Departments />
            </ProtectedRoute>
          ),
        },
        {
          path: "/services",
          element: (
            <ProtectedRoute permissionId={44}>
              <Services />
            </ProtectedRoute>
          ),
        },
        {
          path: "/parts",
          element: (
            <ProtectedRoute permissionId={118}>
              <Parts />
            </ProtectedRoute>
          ),
        },
        {
          path: "/packages",
          element: (
            <ProtectedRoute permissionId={50}>
              <Packages />
            </ProtectedRoute>
          ),
        },
        {
          path: "/packages/create",
          element: (
            <ProtectedRoute permissionId={51}>
              <CreatePackage />
            </ProtectedRoute>
          ),
        },
        {
          path: "/packages/create/:packageId",
          element: (
            <ProtectedRoute permissionId={54}>
              <CreatePackage />
            </ProtectedRoute>
          ),
        },
        {
          path: "/orders",
          element: (
            <ProtectedRoute permissionId={62}>
              <ProductOrders />
            </ProtectedRoute>
          ),
        },
        {
          path: "/orders/:id",
          element: (
            <ProtectedRoute permissionId={64}>
              <ViewProductOrder />
            </ProtectedRoute>
          ),
        },
        {
          path: "/subscriptions",
          element: (
            <ProtectedRoute permissionId={65}>
              <Subscriptions />
            </ProtectedRoute>
          ),
        },
        {
          path: "/request-edit-address",
          element: (
            <ProtectedRoute permissionId={102}>
              <EditAddressRequest />
            </ProtectedRoute>
          ),
        },
        {
          path: "/cancellation-requests",
          element: (
            <ProtectedRoute permissionId={105}>
              <CancellationRequests />
            </ProtectedRoute>
          ),
        },
        {
          path: "/inspection-requests",
          element: (
            <ProtectedRoute permissionId={130}>
              <InspectionRequests />
            </ProtectedRoute>
          ),
        },
        {
          path: "/discount-codes",
          element: (
            <ProtectedRoute permissionId={72}>
              <DiscountCodes />
            </ProtectedRoute>
          ),
        },
        {
          path: "/notifications",
          element: (
            <ProtectedRoute permissionId={78}>
              <Notifications />
            </ProtectedRoute>
          ),
        },
        {
          path: "/notifications/create",
          element: (
            <ProtectedRoute permissionId={79}>
              <CreateNotification />
            </ProtectedRoute>
          ),
        },
        {
          path: "/messages",
          element: (
            <ProtectedRoute permissionId={82}>
              <Messages />
            </ProtectedRoute>
          ),
        },
        {
          path: "/messages/create",
          element: (
            <ProtectedRoute permissionId={84}>
              <CreateMessage />
            </ProtectedRoute>
          ),
        },
        {
          path: "/ratings",
          element: (
            <ProtectedRoute permissionId={86}>
              <Ratings />
            </ProtectedRoute>
          ),
        },
        {
          path: "/support",
          element: (
            <ProtectedRoute permissionId={90}>
              <Support />
            </ProtectedRoute>
          ),
        },
        {
          path: "/settings",
          element: (
            <ProtectedRoute permissionId={93}>
              <Settings />
            </ProtectedRoute>
          ),
        },
        // Profile is usually accessible to all authenticated users
        {
          path: "/profile",
          element: <Profile />,
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}

export default App;
